import {useIsOverviewEnabled} from '../../hooks/use-feature-state'
import {memo, useRef} from 'react'
import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {getPanelHeading} from './panel-registry'
import {TimelinePanel} from './timeline-panel/TimelinePanel'
import {TimelineFilter, TimelineFilterProvider} from './timeline-panel/FilteredTimeline'
import {usePullRequestBasePath} from '../../stores/selectors'

/**
 * Renders the overview "Activity" sidebar inside the centralized docked side
 * panel. Mounted by `DockedSidePanelContent` when `DockedPanel.Timeline` is
 * open and the `overview` feature state is enabled. In compact mode, returns
 * `CompactPanel` slots picked up by `CompactSidePanelDialog`; with
 * the Overview preview not enabled, falls back to a legacy per-panel
 * `<Dialog>` sidesheet.
 */
export const OverviewTimelinePanel = memo(function OverviewTimelinePanelImpl() {
  const isOpen = useIsDockedPanelOpen(DockedPanel.Timeline)
  const pathName = usePullRequestBasePath()
  if (!isOpen) return null

  return isFeatureEnabled('prs_timeline_filters') ? (
    <TimelineFilterProvider key={pathName}>
      <OverviewTimelinePanelLayout />
    </TimelineFilterProvider>
  ) : (
    <OverviewTimelinePanelLayout />
  )
})

function OverviewTimelinePanelLayout() {
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const updatedPullRequestHeader = useIsOverviewEnabled()
  const filter = isFeatureEnabled('prs_timeline_filters') ? <TimelineFilter /> : null

  if (!isCompact) {
    return (
      <Panel panelName={DockedPanel.Timeline}>
        {filter && <Panel.Header>{filter}</Panel.Header>}
        <Panel.Body>
          <TimelinePanel />
        </Panel.Body>
      </Panel>
    )
  }

  if (!updatedPullRequestHeader) {
    return (
      <Dialog
        initialFocusRef={closeButtonRef}
        position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
        returnFocusRef={returnFocusRef}
        onClose={closePanel}
        renderHeader={({dialogLabelId}) => (
          <Dialog.Header>
            <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
              <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.Timeline)}</Dialog.Title>
              <IconButton
                ref={closeButtonRef}
                aria-label="Close activity panel"
                icon={XIcon}
                variant="invisible"
                onClick={closePanel}
              />
            </div>
            {filter}
          </Dialog.Header>
        )}
      >
        <TimelinePanel />
      </Dialog>
    )
  }

  return (
    <>
      {filter && <CompactPanel.Header>{filter}</CompactPanel.Header>}
      <CompactPanel.Content>
        <TimelinePanel />
      </CompactPanel.Content>
    </>
  )
}
